import * as React from "react";
import {useDispatch, useSelector} from "react-redux";
import {getParseOptions, setParseOptions, setSaveToConfig} from "../slices/ruleSlice";
import {ChangeEvent, useEffect, useState} from "react";
import "./OptionsForm.css"
import {Icon, TextArea} from "semantic-ui-react";

interface OptionsEditFormProps{

}

const AI_MODELS = [
    { value: "GPT4_O", label: "GPT-4o (Default / OpenAI)" },
    { value: "GPT5", label: "GPT-5" },
    { value: "GPT5_MINI", label: "GPT-5 Mini" },
    { value: "Claude4_Sonnet", label: "Claude 4 Sonnet" },
    { value: "Claude45_Sonnet", label: "Claude 4.5 Sonnet" },
    { value: "Claude45_Haiku", label: "Claude 4.5 Haiku" },
    { value: "GEMINI25_PRO", label: "Gemini 2.5 Pro" },
    { value: "GEMINI25_FLASH", label: "Gemini 2.5 Flash" },
] as const;

export function OptionsEditForm(props:React.PropsWithChildren<OptionsEditFormProps>){
    const options = useSelector(getParseOptions)
    const dispatch = useDispatch()

    const deriveState = () => {
        if(!options){
            console.log("********** General Options are not set! **********")
        }
        return {
            dateFormat: options?.dateFormat??"dd.MM.yyyy",
            timeFormat: options?.timeFormat??"hh:mm",
            defaultArriveHour: options?.defaultArriveHour??0,
            defaultDepartHour: options?.defaultDepartHour??23,
            lengthUnit: options?.lengthUnit ?? "m",
            weightUnit: options?.weightUnit ?? "kg",
            numberFormat: options?.numberFormat ?? "german",
            stopListMode: options?.stopListMode ?? false,
            aiPrompts: options?.aiPrompts,
            genericAiPromptAdditionalInstructions: options?.genericAiPromptAdditionalInstructions ??"",
            aiModel: options?.aiModel ?? "GPT4_O"
        }
    }

    const [formState, setFormState] = useState(deriveState())
    const [isInitialized, setIsInitialized] = useState(false)

    useEffect(() => {
        if (!isInitialized) {
            setFormState(deriveState())
            setIsInitialized(true)
        }
    }, [options, isInitialized])

    const persistSoon = React.useCallback(
        () => setTimeout(() => dispatch(setSaveToConfig(true)), 1000),
        [dispatch]
    );

    const handleEvent = (evt:ChangeEvent<HTMLInputElement|HTMLSelectElement>)=>{
        let valueToSet : any = evt.target.value
        if(evt.target.type === 'checkbox' && "checked" in evt.target)
            valueToSet = evt.target.checked
        if(evt.target.type === 'number')
            valueToSet = parseFloat(evt.target.value)
        const nextState = {
            ...formState,
            [evt.target.name]: valueToSet
        }
        setFormState(nextState)
        dispatch(setParseOptions(nextState))
        persistSoon()
    }


    return <>
        <form>
            <fieldset>
                <legend>Date & Time</legend>
                <div className="formGrid">
                    <label>Date Format</label>
                    <input type={"text"} value={formState.dateFormat} name={"dateFormat"} onChange={handleEvent}/>

                    <label>Time Format</label>
                    <input type={"text"} value={formState.timeFormat} name={"timeFormat"} onChange={handleEvent}/>

                    <label>Default Arrive At Hour</label>
                    <input min={0} max={23} type={"number"} value={formState.defaultArriveHour} name={"defaultArriveHour"} onChange={handleEvent}/>

                    <label>Default Depart At Hour</label>
                    <input min={0} max={23} type={"number"} value={formState.defaultDepartHour} name={"defaultDepartHour"} onChange={handleEvent}/>
                </div>
            </fieldset>
            <fieldset>
                <legend>Number Format</legend>
                <div className="formGrid">
                    <label>Number Format</label>
                    <select value={formState.numberFormat} name={"numberFormat"} onChange={handleEvent}>
                        <option key={"german"} value={"german"}>German (, as Decimal separator)</option>
                        <option key={"english"} value={"english"}>English (. as Decimal separator)</option>
                        <option key={"french"} value={"french"}>French</option>
                        <option key={"auto"}>Auto Detect / Handle with care</option>
                    </select>
                </div>
            </fieldset>
            <fieldset>
                <legend>Units</legend>
                <div className="formGrid">
                    <label>Length Unit</label>
                    <select value={formState.lengthUnit} name={"lengthUnit"} onChange={handleEvent}>
                        <option key={"mm"} value={"mm"}>mm</option>
                        <option key={"cm"} value={"cm"}>cm</option>
                        <option key={"m"} value={"m"}>m</option>
                    </select>

                    <label>Weight Unit</label>
                    <select value={formState.weightUnit} name={"weightUnit"} onChange={handleEvent}>
                        <option key={"g"} value={"g"}>g</option>
                        <option key={"kg"} value={"kg"}>kg</option>
                        <option key={"t"} value={"t"}>t</option>
                    </select>
                </div>
            </fieldset>
            <fieldset>
                <legend>Other</legend>
                <div className={"formGrid"}>
                    <label>Allow more than one loading/unloading stop per transport</label>
                    <input name={"stopListMode"} type="checkbox" onChange={handleEvent} checked={formState.stopListMode}/>

                    <label>Generic AI Additional Instructions</label><br/>
                    <TextArea style={{width: "100%", marginBottom: "10px", marginRight: "10px", marginLeft: "10px"}}
                              rows={8}
                              value={formState.genericAiPromptAdditionalInstructions}
                              name={"genericAiPromptAdditionalInstructions"}
                              onChange={(e) => {
                                  const nextState = {
                                      ...formState,
                                      [e.currentTarget.name]: e.currentTarget.value
                                  }
                                  setFormState(nextState)
                                  dispatch(setParseOptions(nextState))
                                  persistSoon()
                              }}/>
                </div>
            </fieldset>
            <fieldset>
                <legend>AI Model Selection</legend>
                <div className="formGrid">
                    <label>
                        AI Model
                        <Icon
                            name="info circle"
                            color="blue"
                            size="small"
                            style={{marginLeft: "5px", cursor: "help"}}
                            title="Model selected from here will be added to the config automatically. Note: Legacy, GPT-3.5, GPT-4, GPT-4 Turbo, GPT4O-mini, Claude 3.5/3.7, and Mistral are not fully supported Even if they appear in the config tab - the fallback used is gpt4O"
                        />
                    </label>
                    <select value={formState.aiModel} name={"aiModel"} onChange={handleEvent}>
                        {AI_MODELS.map((model) => (
                            <option key={model.value} value={model.value}>
                                {model.label}
                            </option>
                        ))}
                    </select>
                </div>
            </fieldset>
        </form>

        <button onClick={(e)=>{e.preventDefault(); dispatch(setParseOptions(formState)); dispatch(setSaveToConfig(true))}}>Save</button>
    </>
}